/**
 * `GET /v1/stores` e `GET /v1/stores/{slug}` — filiais, na superficie PUBLICA.
 *
 * Fontes: `CONTRACTS §8.2` (bloco "Publico e lojas", 6 linhas de rota), `API §6.5` (o que cada
 * uma devolve), `API §8.1` (cache) e `DATA_MODEL §3.2` (as colunas de `store`,
 * `store_business_hours` e `store_hours_exception`).
 *
 * Duas coisas que este arquivo decide e que valem ser lidas antes do codigo:
 *
 * **1. Por que dado pessoal do farmaceutico aparece em rota `PUB`.** `API §6.5` lista
 * "farmaceutico RT + CRF" no corpo de `GET /v1/stores`, e REG-001 exige a identificacao
 * sanitaria exibida por filial — nome do responsavel tecnico, CRF, licenca sanitaria e AFE sao
 * **publicacao obrigatoria**, nao vazamento. E a unica excecao do produto a regra de que rota
 * publica nao carrega `@pii{c:DIRECT}`, e ela existe porque a lei manda publicar. Nenhum outro
 * campo de `staff_user` atravessa: nem e-mail, nem telefone, nem id.
 *
 * **2. Por que nao ha preco, estoque nem produto aqui.** Filial e configuracao da rede
 * (ARCHITECTURE §8.1: "Horario, plantao, zona de entrega — escopo FILIAL"). Preco e
 * disponibilidade dependem de `storeId` mas vivem em `catalog`/`pricing`, com cache
 * radicalmente diferente (`no-store` contra `s-maxage=600`). Misturar os dois numa resposta
 * so poria estoque numa resposta cacheavel por 10 minutos.
 */
import { z } from 'zod';

import {
  CepSchema,
  InstantSchema,
  LocalDateSchema,
  PhoneBrE164Schema,
  SlugSchema,
  UfSchema,
  Uuidv7Schema,
  paginated,
} from '../common';

// ---------------------------------------------------------------------------
// Primitivos de horario
// ---------------------------------------------------------------------------

/** 0 = domingo .. 6 = sabado — a convencao de `store_business_hours.weekday`. */
export const WeekdaySchema = z.number().int().min(0).max(6);
export type Weekday = z.infer<typeof WeekdaySchema>;

/** `HH:MM` em 24 h, hora LOCAL da filial (`store_business_hours.opensAt VarChar(5)`). */
export const HhMmSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Horario deve ser HH:MM em 24 h (DATA_MODEL §3.2).');
export type HhMm = z.infer<typeof HhMmSchema>;

/**
 * Codigo da filial: `"F01".."Fnn"` (`store.code`, DATA_MODEL §3.2). Aparece no ticket e no
 * codigo do pedido, entao o formato e contrato, nao detalhe.
 */
export const StoreCodeSchema = z
  .string()
  .regex(/^[A-Z]\d{2,4}$/, 'Codigo de filial invalido: esperado F01..Fnn (DATA_MODEL §3.2).');
export type StoreCode = z.infer<typeof StoreCodeSchema>;

// ---------------------------------------------------------------------------
// Estado de abertura — espelha `StoreOpenState` de `@farmacia/domain`
// ---------------------------------------------------------------------------

/**
 * Os motivos de fechamento. Sao os mesmos valores do union `StoreClosedReason` de
 * `@farmacia/domain/store` — a regra e la, o contrato de wire e aqui, e o teste de
 * conformidade (`stores.domain-conformance.test.ts`) prova que as duas listas nao divergem.
 */
export const STORE_CLOSED_REASONS = [
  'INATIVA',
  'FECHAMENTO_EXCEPCIONAL',
  'FORA_DO_HORARIO',
  'SEM_HORARIO_CADASTRADO',
] as const;

export const StoreClosedReasonSchema = z.enum(STORE_CLOSED_REASONS);
export type StoreClosedReason = z.infer<typeof StoreClosedReasonSchema>;

/**
 * "Aberta agora?" calculada no SERVIDOR, no fuso de `identity.timezone` do perfil.
 *
 * O PWA recalcula a mesma coisa com `resolveStoreOpenState` de `@farmacia/domain` quando a
 * resposta vem do cache do service worker (API §8.1 permite `s-maxage=600` nesta rota) — e por
 * isso `businessHours` e `hoursExceptions` viajam no detalhe: sem elas, o app so saberia
 * repetir um `open` de dez minutos atras.
 */
export const StoreOpenStatusSchema = z.object({
  open: z.boolean(),
  /** `open` **e** nao pausada. E este campo que o checkout consulta, nao `open`. */
  acceptingOrders: z.boolean(),
  /** "Modo ocupado" do Painel da Loja (P7). */
  paused: z.boolean(),
  pausedUntil: InstantSchema.nullable(),
  /** Janela vigente (ou a proxima, se fechada) e de plantao — R-23 / P8. */
  onCall: z.boolean(),
  closedReason: StoreClosedReasonSchema.nullable(),
  /** Fim da janela vigente. `null` quando fechada. */
  closesAt: InstantSchema.nullable(),
  /** Proxima abertura. `null` quando aberta, ou quando nao ha janela nos proximos 7 dias. */
  opensAt: InstantSchema.nullable(),
});
export type StoreOpenStatus = z.infer<typeof StoreOpenStatusSchema>;

// ---------------------------------------------------------------------------
// Endereco e identificacao sanitaria
// ---------------------------------------------------------------------------

/**
 * Endereco da filial. **Nao e o endereco do cliente** — nao tem `reference`, nao tem
 * `zoneId` e nao e dado pessoal: e o endereco comercial que a licenca sanitaria publica.
 */
export const StoreAddressSchema = z.object({
  street: z.string().min(1),
  number: z.string().nullable(),
  district: z.string().nullable(),
  city: z.string().min(1),
  uf: UfSchema,
  /** Informativo, nunca chave (R-28). Ha municipio com CEP unico e ha filial sem CEP. */
  postalCode: CepSchema.nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
});
export type StoreAddress = z.infer<typeof StoreAddressSchema>;

/**
 * Farmaceutico responsavel tecnico. REG-001 / REG-020: nome e CRF sao de **publicacao
 * obrigatoria**. O `id` do `staff_user` NAO sai — ele so serviria para correlacionar pessoa com
 * pedido a partir de fora.
 */
export const ResponsiblePharmacistSchema = z.object({
  fullName: z.string().min(1),
  crfNumber: z.string().min(1),
  crfUf: UfSchema,
});
export type ResponsiblePharmacist = z.infer<typeof ResponsiblePharmacistSchema>;

/** REG-001: licenca sanitaria e AFE, exibidas no rodape e na pagina institucional. */
export const SanitaryIdentificationSchema = z.object({
  licenseNumber: z.string().min(1),
  licenseExpiresAt: LocalDateSchema,
  /** Autorizacao de Funcionamento de Empresa. Nem toda filial tem. */
  afeNumber: z.string().nullable(),
  responsiblePharmacist: ResponsiblePharmacistSchema.nullable(),
});
export type SanitaryIdentification = z.infer<typeof SanitaryIdentificationSchema>;

// ---------------------------------------------------------------------------
// `GET /v1/stores` — o resumo
// ---------------------------------------------------------------------------

export const StoreSummarySchema = z.object({
  id: Uuidv7Schema,
  /**
   * Chave do caminho de `GET /v1/stores/{slug}` (CONTRACTS §8.2).
   *
   * DUVIDA (reportada): `DATA_MODEL §3.2` **nao tem coluna `slug` em `store`** — a unica chave
   * humana unica e `store.code` ("F01"). CONTRACTS §8.2 e API §6.5, que estao acima de
   * DATA_MODEL na precedencia para a FORMA da rota, nomeiam o parametro `{slug}`. A
   * reconciliacao aplicada aqui nao inventa coluna: `slug` e `code` em minusculas, derivado e
   * estavel, e a rota resolve por ele. Se o produto quiser slug editavel ("centro", "matriz"),
   * isso e uma coluna nova em `store` e um PR que muda DATA_MODEL primeiro.
   */
  slug: SlugSchema,
  code: StoreCodeSchema,
  /** Nome de vitrine da filial (`store.name`). */
  name: z.string().min(1),
  /** Razao social da REDE (`organization.legalName`) — a mesma para todas as filiais. */
  legalName: z.string().min(1),
  /** CNPJ **da filial**, so digitos. Cada filial tem o seu (`store.cnpj`). */
  cnpj: z.string().regex(/^\d{14}$/, 'CNPJ deve ter 14 digitos, sem pontuacao.'),
  address: StoreAddressSchema,
  phone: PhoneBrE164Schema,
  whatsappPhone: PhoneBrE164Schema.nullable(),
  sanitary: SanitaryIdentificationSchema,
  acceptsDelivery: z.boolean(),
  acceptsPickup: z.boolean(),
  status: StoreOpenStatusSchema,
});
export type StoreSummary = z.infer<typeof StoreSummarySchema>;

/**
 * Envelope de colecao `{ data, page }` (API §1.1).
 *
 * DUVIDA (reportada): API §1.4 diz "toda colecao pagina por cursor", mas `CONTRACTS §8.2` nao
 * da parametro de cursor a esta rota, e a colecao e **fechada** — ela vem de `stores[]` do
 * `DeploymentProfile`, validado no boot, e o envelope de porte do produto (ADR-0023) fala em
 * unidades de filiais, nao centenas. A rota devolve a lista INTEIRA numa pagina, com
 * `hasMore: false`, e `limit` reflete o tamanho efetivo — o envelope nunca mente. O codec de
 * cursor assinado por HMAC de API §1.4 nasce com a primeira colecao ilimitada
 * (`catalog/products`), junto com o segredo que ele exige, que hoje nao existe no envelope de
 * ambiente. Truncar silenciosamente seria pior que as duas coisas.
 */
export const StoreListResponseSchema = paginated(StoreSummarySchema);
export type StoreListResponse = z.infer<typeof StoreListResponseSchema>;

// ---------------------------------------------------------------------------
// `GET /v1/stores/{slug}` — o detalhe
// ---------------------------------------------------------------------------

/** Uma linha de `store_business_hours`, ja agrupada por dia da semana. */
export const BusinessHoursWindowSchema = z.object({
  opensAt: HhMmSchema,
  closesAt: HhMmSchema,
  /** Escala de plantao (P8) — a janela e de sobreaviso, nao de horario comercial. */
  isOnCall: z.boolean(),
});
export type BusinessHoursWindow = z.infer<typeof BusinessHoursWindowSchema>;

export const BusinessHoursDaySchema = z.object({
  weekday: WeekdaySchema,
  /** Vazio = fechada naquele dia. Mais de uma janela = fecha para o almoco. */
  windows: z.array(BusinessHoursWindowSchema),
});
export type BusinessHoursDay = z.infer<typeof BusinessHoursDaySchema>;

/** Uma linha de `store_hours_exception`: feriado municipal, inventario, plantao de data. */
export const HoursExceptionSchema = z.object({
  date: LocalDateSchema,
  closed: z.boolean(),
  opensAt: HhMmSchema.nullable(),
  closesAt: HhMmSchema.nullable(),
  isOnCall: z.boolean(),
  /** Texto editorial curto: "feriado municipal", "plantao", "inventario". */
  reason: z.string().min(1),
});
export type HoursException = z.infer<typeof HoursExceptionSchema>;

/**
 * Janela de excecoes devolvida pelo detalhe, em dias a frente da data corrente.
 *
 * DUVIDA (reportada): nem API §6.5 nem DATA_MODEL fixam o horizonte. 60 dias cobre o proximo
 * feriado municipal em qualquer calendario brasileiro sem transformar a resposta — cacheada
 * por 10 min na borda (API §8.1) — num despejo do historico de feriados da filial.
 */
export const HOURS_EXCEPTION_HORIZON_DAYS = 60 as const;

export const StoreDetailSchema = StoreSummarySchema.extend({
  /**
   * Sete entradas, domingo a sabado, **sempre** — inclusive os dias fechados, com `windows`
   * vazio. Omitir o dia fechado obrigaria todo cliente a distinguir "fechada" de "nao sei".
   */
  businessHours: z.array(BusinessHoursDaySchema).length(7),
  /** Proximos `HOURS_EXCEPTION_HORIZON_DAYS` dias, em ordem crescente de data. */
  hoursExceptions: z.array(HoursExceptionSchema),
});
export type StoreDetail = z.infer<typeof StoreDetailSchema>;

/** Parametro de caminho de `GET /v1/stores/{slug}`. */
export const StoreSlugParamSchema = z.object({ slug: SlugSchema });
export type StoreSlugParam = z.infer<typeof StoreSlugParamSchema>;

/**
 * `Cache-Control` das duas rotas — API §8.1, linha "`GET /v1/stores`, `/v1/stores/{slug}`".
 *
 * Fica aqui, e nao numa constante do controller, porque o valor e **contrato**: o service
 * worker do PWA depende dele (allowlist de PWA §4.2 R10) e o teste de integracao o compara
 * literalmente. Uma segunda grafia em outro arquivo e como a divergencia comeca.
 */
export const PUBLIC_STORES_CACHE_CONTROL = 'public, s-maxage=600, stale-while-revalidate=3600';
